import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { API } from 'api';
import { WeatherStationObservation } from '../../weatherStations.types';
import {
  ServerObservationValues,
  transformObservationValues,
} from '../../utils/transformObservationValues';

type UseWeatherStationObservationsProps = {
  id: string | number;
  start: Date;
  end: Date;
  pollInterval?: number;
  maxResults?: number;
};

type UseWeatherStationObservations = Omit<
  UseQueryResult<WeatherStationObservation[], Error>,
  'data'
> & {
  observations: WeatherStationObservation[];
};

type ServerWeatherStationObservation = {
  timestamp: string;
  values: ServerObservationValues;
};

type FetchObservationsValues = {
  id: string | number;
  start: Date;
  end: Date;
  maxResults?: number;
};

const fetchWeatherStationObservations = async (
  values: FetchObservationsValues,
): Promise<WeatherStationObservation[]> => {
  const { id, start, end, maxResults } = values;
  const response = await API.get<ServerWeatherStationObservation[]>(
    `weather/observations/${id}/`,
    {
      params: {
        start: start.toISOString(),
        end: end.toISOString(),
        ...(maxResults !== undefined && { max_results: maxResults }),
      },
    },
  );

  return response.data.map((observation) => ({
    timestamp: new Date(observation.timestamp),
    ...transformObservationValues(observation.values),
  }));
};

export const useWeatherStationObservations = (
  props: UseWeatherStationObservationsProps,
): UseWeatherStationObservations => {
  const { id, start, end, pollInterval, maxResults } = props;

  const { data: observations = [], ...results } = useQuery({
    queryKey: [
      'weatherStationObservations',
      id,
      start.toISOString(),
      end.toISOString(),
      maxResults,
    ],
    queryFn: () =>
      fetchWeatherStationObservations({ id, start, end, maxResults }),
    refetchInterval: pollInterval,
  });

  return {
    ...results,
    observations,
  };
};
